<template>
  <div
    v-if="$can('read', 'ShareTargetTransaction')"
    style="height: inherit"
  >
    <b-overlay
      :show="showOverlay"
      rounded="sm"
    >
      <!-- Filters -->
      <share-target-transaction-list-filters
        :collapsed-filter.sync="collapsedFilter"
        :name-filter.sync="nameFilter"
        @reset-filter="resetFilter"
      />

      <!-- Table Container Card -->
      <b-card
        no-body
      >
        <div class="m-2">

          <!-- Table Top -->
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              sm="12"
              md="6"
            >
              <label>{{ $t('Entries') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-50 mr-1"
              />
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              sm="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end w-100 mt-1 mt-md-0">
                <b-button
                  v-if="$can('write', 'ShareTargetTransaction')"
                  variant="primary"
                  class="w-50"
                  @click="exportShareTargetTransactionForm"
                >
                  {{ `${$t('Export')} (${$t('Excel File')})` }}
                </b-button>
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1 ml-1"
                  :placeholder="`${$t('Search')}...`"
                />
              </div>
            </b-col>
          </b-row>

        </div>
        <b-table
          ref="refShareTargetTransactionListTable"
          :items="getListShareTargetTransaction"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          hover
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
          class="position-relative"
          selectable
          select-mode="multi"
        >
          <!-- Column: Date/Time Create / ID -->
          <template #cell(create_datetime)="data">
            <!-- Date/Time -->
            <div
              style="margin-top: 0.3rem;"
            >
              <b-list-group>
                <!-- Create Date/Time -->
                <b-list-group-item
                  v-if="data.item.create_date_show !== undefined"
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ `${$t('Create')}` }} »
                  </span>
                  <span style="margin-right: 0.4rem;">
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-secondary` : `light-secondary` "
                      class="text-capitalize"
                    >
                      {{ data.item.create_date_show }}
                    </b-badge>
                  </span>
                  <span style="margin-left: 0.6rem;margin-right: 0.4rem;">
                    <feather-icon
                      icon="ClockIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-secondary` : `light-secondary` "
                      class="text-capitalize"
                    >
                      {{ data.item.create_time_show }}
                    </b-badge>
                  </span>
                </b-list-group-item>
                <!-- Result Date/Time -->
                <b-list-group-item
                  v-if="data.item.result_date_show !== undefined"
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ `${$t('Result')}` }} »
                  </span>
                  <span style="margin-right: 0.4rem;">
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-secondary` : `light-secondary` "
                      class="text-capitalize"
                    >
                      {{ data.item.result_date_show }}
                    </b-badge>
                  </span>
                  <span style="margin-left: 0.6rem;margin-right: 0.4rem;">
                    <feather-icon
                      icon="ClockIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-secondary` : `light-secondary` "
                      class="text-capitalize"
                    >
                      {{ data.item.result_time_show }}
                    </b-badge>
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <!-- ID -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ $t('ID') }} »
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-info` : `light-info` "
                      class="text-capitalize"
                    >
                      {{ data.item.id }}
                    </b-badge>
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: Line User >> DisplayName/UserId/Language/OS -->
          <template #cell(line_user)="data">
            <!-- Display Name -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.4rem;">
                    <b-avatar
                      :src="data.item.picture_url"
                      size="30"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    {{ data.item.display_name }}
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <!-- User ID -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="TagIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">{{ data.item.user_id }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <!-- Language / OS -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="FlagIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">{{ data.item.language }}</span>
                  <span style="margin-left: 1.5rem; margin-right: 0.6rem;">
                    <feather-icon
                      icon="GlobeIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">{{ $t(textFirstUpper(data.item.os)) }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: ShareTarget Message/Link -->
          <template #cell(sharetarget)="data">
            <!-- Name -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ $t('Share Target Name') }} »
                  </span>

                  <span style="font-size: 14px;">
                    {{ data.item.sharetarget_name }}
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <!-- Message -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ $t('Target Message') }} »
                  </span>

                  <span :style="(data.item.share_message !== '') ? 'color: #ff0000;' : 'color: #000000;'">
                    <feather-icon
                      :icon="(data.item.share_message !== '') ? 'FileTextIcon' : 'FileIcon'"
                      class="cursor-pointer mr-1"
                      size="16"
                      @click="onShowMessage(data)"
                    />
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <!-- Link -->
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    {{ $t('Link') }} »
                  </span>
                  <div>
                    <b-link
                      :href="data.item.sharetarget_link"
                      target="_blank"
                    >
                      {{ data.item.sharetarget_link }}
                    </b-link>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: Result -->
          <template #cell(result)="data">
            <!-- Result -->
            <div style="margin-top: 0.3rem;">
              <span style="font-size: 14px;">
                <b-badge
                  pill
                  :variant="(skin === 'dark')? `light-${isResultVariant(data.item)}` : `light-${isResultVariant(data.item)}` "
                  class="text-capitalize"
                >
                  {{ $t(isResultToText(data.item)) }}
                </b-badge>
              </span>
            </div>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">

            <div class="text-nowrap">
              <feather-icon
                v-if="$can('read', 'ShareTargetTransaction')"
                :id="`share-target-transaction-row-${data.item.id}-view-icon`"
                icon="EyeIcon"
                size="16"
                class="cursor-pointer mr-1"
                @click="viewShareTargetTransactionForm(data)"
              />
              <b-tooltip
                v-if="$can('read', 'ShareTargetTransaction')"
                :title="`${$t('View')} ${$t('Share Target')}`"
                :target="`share-target-transaction-row-${data.item.id}-view-icon`"
                :delay="{ show: 2500, hide: 50 }"
                placement="left"
              />

              <feather-icon
                v-if="$can('delete', 'ShareTargetTransaction')"
                :id="`share-target-transaction-row-${data.item.id}-delete-icon`"
                icon="TrashIcon"
                variant="light-success"
                size="16"
                class="cursor-pointer mr-1"
                @click="deleteShareTargetTransactionForm(data)"
              />
              <b-tooltip
                v-if="$can('delete', 'ShareTargetTransaction')"
                :title="`${$t('Delete')} ${$t('Share Target')}`"
                :target="`share-target-transaction-row-${data.item.id}-delete-icon`"
                :delay="{ show: 2500, hide: 50 }"
                placement="right"
              />

            </div>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                {{ $t('Showing {from} to {to} of {of} entries',
                      {from: dataMeta.from, to: dataMeta.to, of: dataMeta.of })
                }}
              </span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalShareTargetTransactions"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>

      <!-- Form -->
      <share-target-transaction-form
        v-model="shallShowShareTargetTransactionFormModal"
        :share-target-transaction-data="shareTargetTransactionData"
        :type-share-target-transaction-form="typeShareTargetTransactionForm"
        @share-target-transaction-form-update="updateShareTargetTransactionForm"
        @discard-share-target-transaction-form="discardShareTargetTransactionForm"
      />

      <!-- Popup -->
      <share-target-transaction-popup
        v-model="shallShowShareTargetTransactionPopup"
        :popup-data="popupData"
        :title-header-popup="titleHeaderPopup"
        @share-target-transaction-popup-update="updateShareTargetTransactionPopup"
        @discard-share-target-transaction-popup="discardShareTargetTransactionPopup"
      />

      <!-- Export -->
      <share-target-transaction-export
        v-model="shallShowShareTargetTransactionExportModal"
        :share-target-options="shareTargetOptionsFilter"
        @share-target-transaction-export-update="updateShareTargetTransactionExport"
        @discard-share-target-transaction-export="discardShareTargetTransactionExport"
      />

    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput,
  BTable,
  BBadge, BPagination, BTooltip, BListGroup, BListGroupItem,
  BLink,
  BOverlay,
  BAvatar,
  BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { $themeConfig } from '@themeConfig'
import tableShareTargetTransactionList from './tableShareTargetTransactionList'
import storeShareTargetTransaction from './storeShareTargetTransaction'
import ShareTargetTransactionForm from './ShareTargetTransactionForm.vue'
import ShareTargetTransactionListFilters from './ShareTargetTransactionListFilters.vue'
import ShareTargetTransactionPopup from './ShareTargetTransactionPopup.vue'
import ShareTargetTransactionExport from './ShareTargetTransactionExport.vue'

export default {
  components: {
    ShareTargetTransactionListFilters,
    ShareTargetTransactionForm,
    ShareTargetTransactionPopup,
    ShareTargetTransactionExport,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BTooltip,
    BListGroup,
    BListGroupItem,
    BLink,
    BOverlay,
    BAvatar,
    BButton,

    vSelect,
    FeatherIcon,
  },
  data() {
    return {
      showOverlay: false,
      shareTargetTransactionData: {},
      popupData: {},
      dialog: false,
      skin: store.state.appConfig.layout.skin,
      userData: JSON.parse(localStorage.getItem(`${$themeConfig.app.commonSession}-user-data`)),
      selected: [],
      checkSelected: false,
      collapsedFilter: true,
      shareTargetOptions: [],
      shareTargetOptionsFilter: [],
    }
  },
  Computed: {
    checkSelected() {
      return !this.checkSelected
    },
  },
  mounted() {
    this.getDefaultShareTargetTransaction()
    this.locale = this.$i18n.locale
  },
  methods: {
    getDefaultShareTargetTransaction() {
      this.showOverlay = true
      store
        .dispatch('sharetarget-transaction-store/getDefaultShareTargetTransaction', { language: this.$i18n.locale })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.shareTargetTransactionData = response.data
          this.shareTargetOptions = response.data.sharetarget_options
          this.shareTargetOptionsFilter = response.data.sharetarget_options_filter
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response?.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error?.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = true
        })
    },
    viewShareTargetTransactionForm(data) {
      this.showOverlay = true
      store
        .dispatch('sharetarget-transaction-store/getShareTargetTransaction', { language: this.$i18n.locale, id: data.item.id })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.shareTargetTransactionData = response.data
          this.typeShareTargetTransactionForm = 'View'
          this.shallShowShareTargetTransactionFormModal = true
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response?.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error?.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = false
        })
    },
    deleteShareTargetTransactionForm(data) {
      this.$swal({
        title: this.$i18n.t('Are you sure?'),
        text: this.$i18n.t("You won't be able to revert this"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$i18n.t('Yes, delete it'),
        cancelButtonText: this.$i18n.t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value === true) {
          store.dispatch('sharetarget-transaction-store/deleteShareTargetTransaction',
            { id: data.item.id, user_name: this.userData.fullName })
            .then(() => {
              const okMsg = this.$i18n.t('Process on successfully')
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: okMsg,
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              },
              {
                position: 'bottom-right',
              })
              this.$swal({
                icon: 'success',
                title: this.$i18n.t('Deleted'),
                text: this.$i18n.t('Your data has been deleted'),
                timer: 3000,
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              this.refetchData()
            })
            .catch(error => {
              const { response, message } = error
              if (response) {
                let errorMsg = this.$i18n.t(response?.data.error)
                if (errorMsg === '') {
                  errorMsg = this.$i18n.t(response?.data.message)
                }
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: errorMsg,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              } else if (message) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error?.message,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              }
            })
        }
      })
    },
    updateShareTargetTransactionForm() {
      this.refetchData()
    },
    discardShareTargetTransactionForm() {
      this.refetchData()
    },
    resetFilter() {
      this.searchQuery = ''
      this.nameFilter = ''
      const filterList = JSON.parse(localStorage.getItem(`${$themeConfig.app.session}-filter-share-target-transaction-list`))
      if (filterList !== null) {
        this.perPage = (filterList.perPage !== null) ? filterList.perPage : '5'
        this.currentPage = (filterList.page !== null) ? filterList.page : '1'
        this.sortBy = (filterList.sortBy !== null) ? filterList.sortBy : 'id'
        this.isSortDirDesc = (filterList.sortDesc !== null) ? filterList.sortDesc : 'DESC'
      }
    },
    onShowMessage(data) {
      this.showOverlay = true
      store
        .dispatch('sharetarget-transaction-store/getShareTargetTransaction', { language: this.$i18n.locale, id: data.item.id })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.popupData.message = response.data.sharetarget_message
          this.titleHeaderPopup = this.$i18n.t('Target Message')
          this.shallShowShareTargetTransactionPopup = true
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response?.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error?.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = false
        })
    },
    updateShareTargetTransactionPopup() {
    },
    discardShareTargetTransactionPopup() {
    },
    exportShareTargetTransactionForm() {
      this.shallShowShareTargetTransactionExportModal = true
    },
    updateShareTargetTransactionExport() {
      // this.refetchData()
    },
    discardShareTargetTransactionExport() {
      // this.refetchData()
    },
  },
  setup() {
    const shallShowShareTargetTransactionFormModal = ref(false)
    const typeShareTargetTransactionForm = ref('')

    const shallShowShareTargetTransactionPopup = ref(false)
    const titleHeaderPopup = ref('')

    const shallShowShareTargetTransactionExportModal = ref(false)

    // Register module
    if (!store.hasModule('sharetarget-transaction-store')) store.registerModule('sharetarget-transaction-store', storeShareTargetTransaction)
    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule('sharetarget-transaction-store')) store.unregisterModule('sharetarget-transaction-store')
    })

    const {
      getListShareTargetTransaction,
      tableColumns,
      perPage,
      currentPage,
      totalShareTargetTransactions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      locale,
      refShareTargetTransactionListTable,
      refetchData,

      // UI
      isResultVariant,
      isResultToText,
      textFirstUpper,

      nameFilter,
    } = tableShareTargetTransactionList()

    const filterList = JSON.parse(localStorage.getItem(`${$themeConfig.app.session}-filter-share-target-transaction-list`))
    if (filterList !== null) {
      searchQuery.value = (filterList.searchQuery !== null && filterList.searchQuery !== undefined) ? filterList.searchQuery : ''
      perPage.value = (filterList.perPage !== null && filterList.perPage !== undefined) ? filterList.perPage : ''
      currentPage.value = (filterList.page !== null && filterList.page !== undefined) ? filterList.page : ''
      sortBy.value = (filterList.sortBy !== null && filterList.sortBy !== undefined) ? filterList.sortBy : ''
      isSortDirDesc.value = (filterList.sortDesc !== null && filterList.sortDesc !== undefined) ? filterList.sortDesc : ''
      nameFilter.value = (filterList.name !== null && filterList.name !== undefined) ? filterList.name : ''
    }

    return {
      shallShowShareTargetTransactionFormModal,
      typeShareTargetTransactionForm,

      shallShowShareTargetTransactionPopup,
      titleHeaderPopup,

      shallShowShareTargetTransactionExportModal,

      getListShareTargetTransaction,
      tableColumns,
      perPage,
      currentPage,
      totalShareTargetTransactions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      locale,
      refShareTargetTransactionListTable,
      refetchData,

      // UI
      isResultVariant,
      isResultToText,
      textFirstUpper,

      nameFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.share-target-transaction-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import "~@core/scss/base/tools/sharetarget.scss";
</style>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.dark-layout {
  div{
    .b-overlay {
      .bg-light {
        background-color: $theme-dark-body-bg !important;
      }
    }
    .swal2-popup {
      background-color: $theme-dark-body-bg !important;
    }
  }
  .table.b-table > tbody .b-table-row-selected.table-active td {
    background-color: $theme-dark-table-active-bg;
  }
  .table.b-table.table-hover > tbody > tr.table-active:hover td,
  .table.b-table.table-hover > tbody > tr.table-active:hover th {
    background-image: linear-gradient($theme-dark-table-active-hover-bg, $theme-dark-table-active-hover-bg);
  }
}
.b-table-selectable{
  .feather{
    font-size: 1.3rem;
  }
}
</style>
